import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, ArrowLeft, Mail, ArrowRight, ShoppingBag, Instagram, Twitter } from "lucide-react";
import HeroSection from "@/components/storefront/HeroSection";
import { SocialProofBar, BentoFeatures, SpecsGrid } from "@/components/storefront/FeatureSections";
import { UseCases, ReviewsSection, FaqSection } from "@/components/storefront/CommunitySections";
import { PRODUCT_COLORS, PRICE, COMPARE_PRICE } from "@/lib/product-data";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const anim = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

export default function Storefront() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialColor = parseInt(searchParams.get("color")) || 0;
  const [selectedColor, setSelectedColor] = useState(initialColor);
  const [qty, setQty] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [email, setEmail] = useState("");
  const [emailStatus, setEmailStatus] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAddToCart = useCallback(() => {
    setAddedToCart(true);
    setCartCount((p) => p + qty);
    setTimeout(() => setAddedToCart(false), 2500);
  }, [qty]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await axios.post(`${API}/subscribe`, { email });
      setEmailStatus("success");
      setEmail("");
      setTimeout(() => setEmailStatus(null), 4000);
    } catch {
      setEmailStatus("error");
      setTimeout(() => setEmailStatus(null), 4000);
    }
  };

  const color = PRODUCT_COLORS[selectedColor];

  return (
    <div className="bg-[#050B14] min-h-screen text-white relative overflow-hidden font-body">
      {/* Blob Lights */}
      <div className="absolute top-[-5%] left-[20%] w-[500px] h-[500px] bg-cyan-500/15 rounded-full blur-[140px] animate-blob-drift pointer-events-none" />
      <div className="absolute bottom-[30%] right-[15%] w-[400px] h-[400px] bg-blue-600/10 rounded-full blur-[120px] animate-blob-drift pointer-events-none" style={{ animationDelay: "-10s" }} />

      {/* Header */}
      <header data-testid="nav-header" className="sticky top-0 z-50 bg-[#050B14]/60 backdrop-blur-2xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button data-testid="back-home-btn" onClick={() => navigate("/")} className="text-slate-400 hover:text-white transition-colors" aria-label="Back to home">
              <ArrowLeft size={20} />
            </button>
            <span className="font-heading text-2xl font-bold tracking-tighter cursor-pointer" onClick={() => navigate("/")}>
              breeze<span className="text-cyan-400">°</span>
            </span>
          </div>
          <button data-testid="cart-icon" className="relative p-2 bg-white/5 border border-white/10 rounded-xl backdrop-blur-md hover:bg-white/10 transition-colors" aria-label="Cart">
            <ShoppingCart size={20} />
            {cartCount > 0 && (
              <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-cyan-400 text-[#050B14] text-[10px] font-bold rounded-full flex items-center justify-center">
                {cartCount}
              </motion.span>
            )}
          </button>
        </div>
      </header>

      {/* Hero Product */}
      <HeroSection
        selectedColor={selectedColor} setSelectedColor={setSelectedColor}
        qty={qty} setQty={setQty}
        onAddToCart={handleAddToCart} addedToCart={addedToCart}
      />

      <SocialProofBar />
      <BentoFeatures />
      <SpecsGrid />
      <UseCases />
      <div id="reviews"><ReviewsSection /></div>
      <div id="faq"><FaqSection /></div>

      {/* Newsletter */}
      <motion.section data-testid="newsletter-section" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={anim}>
        <div className="bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-3xl p-10 sm:p-16 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 pointer-events-none" />
          <div className="relative z-10">
            <Mail size={36} className="mx-auto mb-4 text-cyan-400" />
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-3">Join 50,000+ Breeze Owners</h2>
            <p className="text-slate-400 text-sm sm:text-base max-w-md mx-auto mb-8">Exclusive drops, cooling tips, and early access to new colors.</p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                data-testid="newsletter-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com" required
                className="flex-1 px-5 py-3.5 bg-white/5 border border-white/10 rounded-full text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:border-cyan-400/30 backdrop-blur-md text-white"
              />
              <button data-testid="newsletter-submit-btn" type="submit" className="px-8 py-3.5 bg-gradient-to-r from-cyan-400 to-blue-500 text-[#050B14] font-bold text-sm rounded-full hover:shadow-[0_0_30px_rgba(34,211,238,0.3)] transition-all flex items-center justify-center gap-2">
                <ArrowRight size={16} /> Subscribe
              </button>
            </form>
            {emailStatus === "success" && <p className="text-cyan-400 text-sm font-semibold mt-4">Welcome to the breeze° family.</p>}
            {emailStatus === "error" && <p className="text-red-400 text-sm font-semibold mt-4">Something went wrong. Try again.</p>}
          </div>
        </div>
      </motion.section>

      {/* Final CTA */}
      <motion.section data-testid="final-cta" className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 text-center" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={anim}>
        <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.9] mb-4">
          Stay Cool<span className="text-cyan-400">.</span><br />Stay Free<span className="text-cyan-400">.</span>
        </h2>
        <p className="text-slate-400 max-w-md mx-auto mb-10">Join 50,000+ people who ditched the handheld fan.</p>
        <motion.button data-testid="final-cta-button" onClick={handleAddToCart} whileTap={{ scale: 0.97 }}
          className={`inline-flex items-center justify-center gap-2.5 py-5 px-12 text-base font-bold rounded-full transition-all duration-300 ${
            addedToCart ? "bg-emerald-500 text-white" : "bg-gradient-to-r from-cyan-400 to-blue-500 text-[#050B14] shadow-[0_0_40px_rgba(34,211,238,0.3)] hover:shadow-[0_0_60px_rgba(34,211,238,0.5)] hover:scale-105"
          }`}
        >
          <ShoppingBag size={20} /> {addedToCart ? "Added!" : `Get Yours — $${PRICE.toFixed(2)}`}
        </motion.button>
      </motion.section>

      {/* Footer */}
      <footer data-testid="footer" className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-12">
            <div>
              <div className="font-heading text-2xl font-bold tracking-tighter mb-3">breeze<span className="text-cyan-400">°</span></div>
              <p className="text-sm text-slate-500 leading-relaxed max-w-xs">Personal cooling, reimagined. Designed for the way you actually live.</p>
              <div className="flex gap-3 mt-6">
                {[Instagram, Twitter].map((Icon, i) => (
                  <a key={i} href="#" className="w-9 h-9 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center text-slate-400 hover:text-cyan-400 hover:border-cyan-400/30 transition-colors backdrop-blur-md">
                    <Icon size={16} />
                  </a>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500 mb-4">Shop</div>
              {["Neck Fans", "Accessories", "Bundles", "Gift Cards"].map((l) => (<a key={l} href="#" className="block text-sm text-slate-400 hover:text-cyan-400 transition-colors mb-2.5">{l}</a>))}
            </div>
            <div>
              <div className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500 mb-4">Support</div>
              {["Shipping Info", "Returns", "Contact Us", "Track Order"].map((l) => (<a key={l} href="#" className="block text-sm text-slate-400 hover:text-cyan-400 transition-colors mb-2.5">{l}</a>))}
            </div>
          </div>
          <div className="border-t border-white/10 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <span className="text-xs text-slate-600">&copy; 2026 breeze° — All rights reserved</span>
            <div className="flex gap-6">{["Privacy", "Terms", "Cookies"].map((l) => (<a key={l} href="#" className="text-xs text-slate-600 hover:text-slate-400 transition-colors">{l}</a>))}</div>
          </div>
        </div>
      </footer>

      {/* Sticky Mobile CTA */}
      <div data-testid="sticky-mobile-cta" className={`fixed bottom-0 left-0 right-0 z-50 bg-[#050B14]/80 backdrop-blur-2xl border-t border-white/10 px-4 py-3 transition-transform duration-300 ${scrollY > 600 ? "translate-y-0" : "translate-y-full"}`}>
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="text-lg font-bold">${PRICE.toFixed(2)}</span>
              <span className="text-sm text-slate-500 line-through">${COMPARE_PRICE.toFixed(2)}</span>
            </div>
            <div className="text-xs text-slate-500 truncate">{color.name} · Qty {qty}</div>
          </div>
          <button data-testid="sticky-add-to-cart-button" onClick={handleAddToCart}
            className={`px-6 py-3 text-sm font-bold rounded-full transition-all whitespace-nowrap flex items-center gap-2 ${
              addedToCart ? "bg-emerald-500 text-white" : "bg-gradient-to-r from-cyan-400 to-blue-500 text-[#050B14] shadow-[0_0_20px_rgba(34,211,238,0.2)]"
            }`}
          >
            <ShoppingBag size={16} /> {addedToCart ? "Added!" : "Add to Cart"}
          </button>
        </div>
      </div>
    </div>
  );
}
